// Loyers (F-048, F-051, F-052) : tableau mensuel des échéances, détail, avis d'échéance, paiement, quittance ou reçu.
import { useEffect, useState } from "react";
import { get, patch, post } from "@core/api";
import { useGet } from "@core/hooks";
import { date, dateTime, money, today } from "@core/format";
import { Button, Card, Empty, ErrorBox, Input, KeyValues, Loading, Modal, MoneyInput, Notice, PageHeader, Select, Stat, Table, fieldError } from "@core/ui";
import { Amount, CallStatus, DocLink, METHOD_LABELS, MonthPicker, RECEIPT_STATUS, SOURCE_LABELS, currentMonth, useAction, useG } from "./shared";
import { sumAllocations } from "./util";

export type Call = {
  id: string;
  lease_id: string;
  period_start: string;
  period_end: string;
  period_label: string;
  due_on: string;
  rent_cents: number;
  charges_cents: number;
  other_cents: number;
  vat_cents: number;
  total_cents: number;
  allocated_cents: number;
  outstanding_cents: number;
  status: string;
  overdue: boolean;
  sent_at: string | null;
  details: any;
  property_label?: string;
  tenants?: string;
  has_quittance?: boolean;
  open_reminders?: number;
};

type MonthData = {
  month: string;
  month_label: string;
  items: Call[];
  totals: { due_cents: number; paid_cents: number; outstanding_cents: number };
  missing: { lease_id: string; property_label: string; tenants: string }[];
};

export function LoyersPage() {
  const { g, can } = useG();
  const [month, setMonth] = useState(currentMonth());
  const q = useGet<MonthData>(g(`/rent-calls?month=${month}`));
  const { busy, run } = useAction();
  const [detail, setDetail] = useState<string | null>(null);
  const [payFor, setPayFor] = useState<{ leaseId: string; callId?: string } | null>(null);
  const canWrite = can("operate") || can("finance");
  const d = q.data;
  return (
    <div className="page">
      <PageHeader
        eyebrow="Gestion"
        title="Loyers"
        intro="Échéances du mois, sommes réglées et restes dus. Une quittance n’est établie qu’après règlement intégral confirmé ; un paiement partiel donne lieu à un reçu."
        actions={
          <div className="gestion-actions">
            <MonthPicker value={month} onChange={setMonth} />
            {canWrite ? (
              <Button busy={busy === "gen"} onClick={() => run("gen", () => post(g("/rent-calls/generate"), { through: month > currentMonth() ? month : undefined }), (r: any) => `${r.outcome.created} échéance(s) créée(s), ${r.outcome.updated} mise(s) à jour.`)}>
                Générer les échéances
              </Button>
            ) : null}
          </div>
        }
      />
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {d ? (
        <>
          <div className="stats">
            <Stat label={`Appelé en ${d.month_label}`} value={money(d.totals.due_cents)} />
            <Stat label="Réglé" value={money(d.totals.paid_cents)} />
            <Stat label="Reste dû" value={money(d.totals.outstanding_cents)} hint={d.totals.outstanding_cents > 0 ? "Vérifiez les paiements reçus avant de relancer." : undefined} />
          </div>
          {d.missing.length ? (
            <Notice tone="attention" title="Échéances non générées">
              {d.missing.map((m) => m.property_label).join(", ")} : aucune échéance pour ce mois. Utilisez « Générer les échéances » (sans doublon : les échéances existantes sont conservées).
            </Notice>
          ) : null}
          <Card title={`Échéances de ${d.month_label}`}>
            {d.items.length === 0 ? (
              <Empty title="Aucune échéance pour ce mois">Les échéances se génèrent à partir des baux actifs : loyer, charges et prorata du premier et du dernier mois.</Empty>
            ) : (
              <Table caption="Échéances du mois">
                <thead>
                  <tr>
                    <th>Logement</th>
                    <th>Locataire</th>
                    <th>Échéance</th>
                    <th className="right">Appelé</th>
                    <th className="right">Reste dû</th>
                    <th>Statut</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.property_label}
                        {c.details?.prorata ? <span className="block muted small">Prorata {c.details.prorata.days} j / {c.details.prorata.month_days} j</span> : null}
                      </td>
                      <td>{c.tenants || "—"}</td>
                      <td>{date(c.due_on)}</td>
                      <td className="right">
                        <Amount cents={c.total_cents} />
                      </td>
                      <td className="right">
                        <Amount cents={c.outstanding_cents} strong={c.overdue} />
                      </td>
                      <td>
                        <CallStatus status={c.status} />
                      </td>
                      <td className="right">
                        <div className="gestion-actions">
                          <Button variant="discret" small onClick={() => setDetail(c.id)}>
                            Détail
                          </Button>
                          {can("finance") && c.outstanding_cents > 0 ? (
                            <Button variant="secondaire" small onClick={() => setPayFor({ leaseId: c.lease_id, callId: c.id })}>
                              Enregistrer un paiement
                            </Button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card>
        </>
      ) : null}
      {detail ? <CallModal callId={detail} onClose={() => setDetail(null)} onPay={(leaseId, callId) => setPayFor({ leaseId, callId })} /> : null}
      {payFor ? <PaymentModal leaseId={payFor.leaseId} callId={payFor.callId} onClose={() => setPayFor(null)} /> : null}
    </div>
  );
}

type CallDetail = Call & {
  reference: string;
  allocations: { payment_id: string; amount_cents: number; received_on: string; method: string; source: string; payer: string; cancelled_at: string | null }[];
  receipts: { id: string; kind: string; number: string; amount_cents: number; status: string; document_id: string | null; sent_at: string | null; cancelled_reason: string }[];
  reminders: { id: string; step: number; kind: string; status: string; sent_at: string | null }[];
};

export function CallModal({ callId, onClose, onPay, orgId }: { callId: string; onClose: () => void; onPay?: (leaseId: string, callId: string) => void; orgId?: string }) {
  const { g, can } = useG(orgId);
  const q = useGet<CallDetail>(g(`/rent-calls/${callId}`));
  const { busy, run } = useAction(orgId);
  const [line, setLine] = useState<{ label: string; cents: number | null; reason: string } | null>(null);
  const c = q.data;
  const lines: { label: string; amount_cents: number }[] = c?.details?.lines ?? [];
  const versions: any[] = c?.details?.versions ?? [];
  const canWrite = can("operate") || can("finance");
  return (
    <Modal open onClose={onClose} title={c ? `Échéance — ${c.period_label}` : "Échéance"}>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error} />
      {c ? (
        <div className="stack">
          <KeyValues
            items={[
              ["Logement", c.property_label],
              ["Période", `du ${date(c.period_start)} au ${date(c.period_end)}`],
              ["Échéance", date(c.due_on)],
              ["Statut", <CallStatus key="s" status={c.status} />],
              ["Référence de virement", <span key="r" className="mono">{c.reference}</span>],
            ]}
          />
          <Table caption="Détail des sommes appelées">
            <tbody>
              <tr>
                <td>Loyer hors charges</td>
                <td className="right">
                  <Amount cents={c.rent_cents} />
                </td>
              </tr>
              <tr>
                <td>{c.details?.charges_mode === "forfait" ? "Charges forfaitaires" : "Provision pour charges"}</td>
                <td className="right">
                  <Amount cents={c.charges_cents} />
                </td>
              </tr>
              {lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.label}</td>
                  <td className="right">
                    <Amount cents={l.amount_cents} />
                  </td>
                </tr>
              ))}
              {c.vat_cents ? (
                <tr>
                  <td>TVA</td>
                  <td className="right">
                    <Amount cents={c.vat_cents} />
                  </td>
                </tr>
              ) : null}
              <tr>
                <td>
                  <strong>Total</strong>
                </td>
                <td className="right">
                  <Amount cents={c.total_cents} strong />
                </td>
              </tr>
            </tbody>
          </Table>
          {c.details?.prorata ? (
            <p className="small muted">
              Prorata : {c.details.prorata.days} jours sur {c.details.prorata.month_days} (jours réels du mois), arrondi au centime le plus proche.
            </p>
          ) : null}
          <Card title="Paiements affectés">
            {c.allocations.length === 0 ? (
              <p className="muted">Aucun paiement affecté.</p>
            ) : (
              <ul className="gestion-list">
                {c.allocations.map((a) => (
                  <li key={a.payment_id} className="gestion-line">
                    <span>
                      {date(a.received_on)} · {METHOD_LABELS[a.method] ?? a.method} · {SOURCE_LABELS[a.source] ?? a.source}
                      {a.payer ? ` · ${a.payer}` : ""}
                      {a.cancelled_at ? <span className="block small muted">Paiement annulé : non pris en compte</span> : null}
                    </span>
                    <Amount cents={a.amount_cents} />
                  </li>
                ))}
              </ul>
            )}
            <p className="small">
              Reste dû : <Amount cents={c.outstanding_cents} strong />
            </p>
          </Card>
          <Card title="Quittances et reçus">
            {c.receipts.length === 0 ? <p className="muted">Aucun document établi.</p> : null}
            <ul className="gestion-list">
              {c.receipts.map((r) => (
                <li key={r.id} className="gestion-line">
                  <span>
                    {r.kind === "quittance" ? "Quittance" : "Reçu"} n° {r.number} · {money(r.amount_cents)} · {RECEIPT_STATUS[r.status] ?? r.status}
                    {r.cancelled_reason ? <span className="block small muted">{r.cancelled_reason}</span> : null}
                  </span>
                  <DocLink id={r.document_id} />
                </li>
              ))}
            </ul>
          </Card>
          {versions.length ? (
            <Card title="Modifications de l’échéance">
              <ul className="gestion-list">
                {versions.map((v, i) => (
                  <li key={i}>
                    {dateTime(v.at)} — {v.reason}
                    {v.sent_before ? " (avis déjà envoyé : renvoyez l’avis corrigé)" : ""}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
          {line ? (
            <Card title="Ajouter une somme à l’échéance">
              <div className="form-row">
                <Input label="Libellé" value={line.label} onChange={(e) => setLine({ ...line, label: e.target.value })} />
                <MoneyInput label="Montant" cents={line.cents} onChange={(v) => setLine({ ...line, cents: v })} />
              </div>
              <Input label="Motif de la modification" value={line.reason} onChange={(e) => setLine({ ...line, reason: e.target.value })} />
              <div className="gestion-actions">
                <Button
                  busy={busy === "line"}
                  onClick={async () => {
                    const ok = await run("line", () => patch(g(`/rent-calls/${c.id}`), { reason: line.reason, add_line: { label: line.label, amount_cents: line.cents ?? 0 } }), "Échéance modifiée ; la modification est tracée.");
                    if (ok) setLine(null);
                  }}
                >
                  Ajouter
                </Button>
                <Button variant="discret" onClick={() => setLine(null)}>
                  Annuler
                </Button>
              </div>
            </Card>
          ) : null}
          {canWrite ? (
            <div className="gestion-actions">
              {c.details?.avis_document_id ? <DocLink id={c.details.avis_document_id}>Avis d’échéance (PDF)</DocLink> : null}
              <Button variant="secondaire" busy={busy === "send"} onClick={() => run("send", () => post(g(`/rent-calls/${c.id}/send`)), (r: any) => r.message)}>
                {c.sent_at ? "Renvoyer l’avis d’échéance" : "Envoyer l’avis d’échéance"}
              </Button>
              {c.allocated_cents > 0 ? (
                <Button
                  variant="secondaire"
                  busy={busy === "receipt"}
                  onClick={() => run("receipt", () => post(g(`/rent-calls/${c.id}/receipt`)), (r: any) => (r.kind === "quittance" ? `Quittance n° ${r.number} établie.` : `Reçu n° ${r.number} établi (paiement partiel).`))}
                >
                  {c.outstanding_cents === 0 ? "Établir la quittance" : "Établir un reçu"}
                </Button>
              ) : null}
              {onPay && can("finance") && c.outstanding_cents > 0 ? (
                <Button variant="secondaire" onClick={() => onPay(c.lease_id, c.id)}>
                  Enregistrer un paiement
                </Button>
              ) : null}
              {c.allocated_cents === 0 && c.status !== "annule" ? (
                <Button variant="discret" onClick={() => setLine({ label: "", cents: null, reason: "" })}>
                  Ajouter une somme
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

type Preview = { open_calls: { id: string; period_label: string; due_on: string; total_cents: number; outstanding_cents: number }[]; allocations: { rent_call_id: string; amount_cents: number }[]; unallocated_cents: number };

/** Saisie d'un paiement : affectation proposée (échéances les plus anciennes d'abord), modifiable avant validation. */
export function PaymentModal({ leaseId, callId, onClose, orgId }: { leaseId: string; callId?: string; onClose: () => void; orgId?: string }) {
  const { g } = useG(orgId);
  const { busy, error, run } = useAction(orgId);
  const [amount, setAmount] = useState<number | null>(null);
  const [form, setForm] = useState({ received_on: today(), method: "virement", payer: "", reference: "", note: "" });
  const [preview, setPreview] = useState<Preview | null>(null);
  const [alloc, setAlloc] = useState<Record<string, number | null>>({});
  const [ver, setVer] = useState(0);
  useEffect(() => {
    if (!amount || amount <= 0) {
      setPreview(null);
      return;
    }
    let live = true;
    get<Preview>(g(`/payments/allocation-preview?lease_id=${leaseId}&amount_cents=${amount}`))
      .then((p) => {
        if (!live) return;
        setPreview(p);
        const next: Record<string, number | null> = {};
        p.open_calls.forEach((c) => (next[c.id] = 0));
        p.allocations.forEach((a) => (next[a.rent_call_id] = a.amount_cents));
        setAlloc(next);
        setVer((v) => v + 1);
      })
      .catch(() => setPreview(null));
    return () => {
      live = false;
    };
  }, [amount, leaseId]);
  const allocated = sumAllocations(alloc);
  const rest = (amount ?? 0) - allocated;
  return (
    <Modal
      open
      onClose={onClose}
      title="Enregistrer un paiement"
      footer={
        <>
          <Button variant="discret" onClick={onClose}>
            Annuler
          </Button>
          <Button
            busy={busy === "pay"}
            disabled={!amount || rest < 0}
            onClick={async () => {
              const allocations = Object.entries(alloc)
                .filter(([, v]) => (v ?? 0) > 0)
                .map(([rent_call_id, v]) => ({ rent_call_id, amount_cents: v }));
              const r = await run("pay", () => post(g("/payments"), { lease_id: leaseId, amount_cents: amount, ...form, allocations }), "Paiement enregistré.");
              if (r) onClose();
            }}
          >
            Enregistrer le paiement
          </Button>
        </>
      }
    >
      <div className="stack">
        <div className="form-row">
          <MoneyInput label="Montant reçu" cents={amount} onChange={setAmount} required error={fieldError(error, "amount_cents")} />
          <Input label="Date de réception" type="date" value={form.received_on} max={today()} onChange={(e) => setForm({ ...form, received_on: e.target.value })} error={fieldError(error, "received_on")} />
        </div>
        <div className="form-row">
          <Select label="Mode" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} options={Object.entries(METHOD_LABELS).map(([value, label]) => ({ value, label }))} help={form.method === "caf" ? "Part versée directement par la CAF (tiers payant)." : undefined} />
          <Input label="Payeur" value={form.payer} onChange={(e) => setForm({ ...form, payer: e.target.value })} help="Un payeur différent du locataire n’est pas une anomalie (colocataire, garant, aide)." />
        </div>
        <Input label="Référence ou libellé" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
        {preview ? (
          <Card title="Affectation aux échéances">
            {preview.open_calls.length === 0 ? <p className="muted">Aucune échéance ouverte : le montant restera un crédit du locataire.</p> : null}
            {preview.open_calls.map((c) => (
              <div key={c.id} className="gestion-alloc">
                <span>
                  {c.period_label} · reste dû {money(c.outstanding_cents)}
                  {c.id === callId ? <span className="block small muted">Échéance sélectionnée</span> : null}
                </span>
                <MoneyInput key={`${c.id}-${ver}`} label={`Affecter à ${c.period_label}`} cents={alloc[c.id] ?? 0} onChange={(v) => setAlloc({ ...alloc, [c.id]: v })} />
              </div>
            ))}
            <p className="small">
              Non affecté (crédit du locataire) : <Amount cents={rest} strong={rest < 0} />
            </p>
            {rest < 0 ? <Notice tone="erreur">Les affectations dépassent le montant reçu.</Notice> : null}
            <ErrorBox error={fieldError(error, "allocations") ? new Error(fieldError(error, "allocations")) : null} />
          </Card>
        ) : null}
      </div>
    </Modal>
  );
}
